import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import Create from "./Create";
import Edit from "./Edit";
import StatusUpdate from "./StatusUpdate";

const List = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogVisible, setCreateDialogVisible] = useState(false);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [statusDialogVisible, setStatusDialogVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [error, setError] = useState("");
  const [highlightedItems, setHighlightedItems] = useState({});
  const toast = useRef(null);

  // Function to highlight an item for 2 seconds
  const highlightItem = (itemId) => {
    // Set the highlight
    setHighlightedItems((prev) => ({
      ...prev,
      [`task-${itemId}`]: true,
    }));

    // Remove the highlight after 2 seconds
    setTimeout(() => {
      setHighlightedItems((prev) => {
        const newState = { ...prev };
        delete newState[`task-${itemId}`];
        return newState;
      });
    }, 2000);
  };

  // Handle adding a new task without refreshing all data
  const handleAddNewTask = (newTaskData) => {
    if (!newTaskData) return;

    // Add the newly created task to the tasks state
    setTasks((prevTasks) => {
      // Make sure we're not adding a duplicate
      const exists = prevTasks.some((task) => task.id === newTaskData.id);
      if (exists) return prevTasks;

      // Highlight the newly created task
      highlightItem(newTaskData.id);

      // Return the updated list with the new task
      return [...prevTasks, newTaskData];
    });

    // Close the create dialog
    setCreateDialogVisible(false);

    // Show success message
    toast.current.show({
      severity: "success",
      summary: "Success",
      detail: "Task created successfully",
      life: 3000,
    });
  };

  // Handle updating a task without refreshing all data
  const handleUpdateTask = (updatedTaskData) => {
    if (!updatedTaskData) return;

    // Update the task in the tasks state
    setTasks((prevTasks) => {
      return prevTasks.map((task) => {
        if (task.id === updatedTaskData.id) {
          // If we only received partial data, merge it with existing task data
          const taskWithUpdates = {
            ...task, // Keep all existing task data
            ...updatedTaskData, // Override with any updated fields
            // Make sure developers are properly structured
            developers: Array.isArray(updatedTaskData.developers)
              ? updatedTaskData.developers
              : updatedTaskData.developer_ids?.map((id) => ({ id })) || task.developers || [],
          };

          // Highlight the updated task
          highlightItem(taskWithUpdates.id);

          return taskWithUpdates;
        }
        return task;
      });
    });

    // Close the edit dialog
    setEditDialogVisible(false);

    // Show success message
    toast.current.show({
      severity: "success",
      summary: "Success",
      detail: "Task updated successfully",
      life: 3000,
    });
  };

  // Handle updating task status without refreshing all data
  const handleUpdateTaskStatus = (updatedTaskData) => {
    if (!updatedTaskData) return;

    // Update the task status in the tasks state
    setTasks((prevTasks) => {
      return prevTasks.map((task) => {
        if (task.id === updatedTaskData.id) {
          // Update status and keep other fields
          const taskWithUpdates = {
            ...task, // Keep all existing task data
            ...updatedTaskData, // Override with any updated fields
          };

          // Highlight the updated task
          highlightItem(taskWithUpdates.id);

          return taskWithUpdates;
        }
        return task;
      });
    });

    // Close the status dialog
    setStatusDialogVisible(false);

    // Show success message
    toast.current.show({
      severity: "success",
      summary: "Success",
      detail: "Task status updated successfully",
      life: 3000,
    });
  };

  // Status options for filtering
  const statusOptions = [
    { label: "All Statuses", value: null },
    { label: "To Do", value: "To Do" },
    { label: "In Progress", value: "In Progress" },
    { label: "Review", value: "Review" },
    { label: "Done", value: "Done" },
  ];

  // Fetch tasks data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await Http.get("/task");
      if (response.data && response.data.data) {
        setTasks(response.data.data);
      } else {
        console.warn("No tasks data found");
        setTasks([]);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setError("Failed to load tasks. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Delete task handler
  const confirmDelete = (task) => {
    confirmDialog({
      message: `Are you sure you want to delete task "${task.title}"?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDelete(task.id),
    });
  };

  const handleDelete = async (taskId) => {
    try {
      // Delete request now includes the current user's ID for recording who deleted the task
      await Http.delete(`/task/${taskId}`);

      // Remove the task from our local state rather than fetching all tasks again
      setTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Task deleted successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to delete task",
        life: 3000,
      });
    }
  };

  // Helper functions for rendering
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getUserName = (user) => {
    if (!user) return "N/A";
    return user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || `ID: ${user.id}`;
  };

  const renderDevelopers = (developers) => {
    if (!developers || developers.length === 0) {
      return <span className="text-muted">None assigned</span>;
    }

    if (developers.length === 1) {
      return <span>{getUserName(developers[0])}</span>;
    }

    return (
      <>
        <span>{getUserName(developers[0])}</span>
        <span className="ms-1 badge bg-secondary">{`+${developers.length - 1} more`}</span>
      </>
    );
  };

  // Template for status column
  const statusBodyTemplate = (rowData) => {
    let statusClassName = "";

    switch (rowData.status) {
      case "To Do":
        statusClassName = "bg-secondary";
        break;
      case "In Progress":
        statusClassName = "bg-primary";
        break;
      case "Review":
        statusClassName = "bg-warning";
        break;
      case "Done":
        statusClassName = "bg-success";
        break;
      default:
        statusClassName = "bg-secondary";
    }

    return <Tag className={statusClassName}>{rowData.status}</Tag>;
  };

  // Template for project/story column
  const projectBodyTemplate = (rowData) => {
    if (rowData.story) {
      return (
        <div>
          <div>{rowData.story.title}</div>
          <small className="text-muted">{rowData.project?.title || "N/A"}</small>
        </div>
      );
    } else {
      return <div>{rowData.project?.title || "N/A"}</div>;
    }
  };

  // Template for task type column
  const taskTypeBodyTemplate = (rowData) => {
    const taskType = rowData.task_type;
    if (!taskType) return <span>N/A</span>;

    return (
      <div className="d-flex align-items-center">
        {taskType.icon && <i className={taskType.icon} style={{ marginRight: "5px", color: taskType.color }}></i>}
        <span>{taskType.task_type}</span>
      </div>
    );
  };

  // Template for label column
  const labelBodyTemplate = (rowData) => {
    const label = rowData.label;
    if (!label) return <span>N/A</span>;

    return (
      <Tag
        style={{
          backgroundColor: label.color || "#ccc",
          color: getContrastColor(label.color),
        }}
      >
        {label.name}
      </Tag>
    );
  };

  // Helper to determine text color based on background color
  const getContrastColor = (hexColor) => {
    if (!hexColor) return "#ffffff";

    hexColor = hexColor.replace("#", "");

    // Convert to RGB
    const r = parseInt(hexColor.substr(0, 2), 16);
    const g = parseInt(hexColor.substr(2, 2), 16);
    const b = parseInt(hexColor.substr(4, 2), 16);

    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > 0.5 ? "#000000" : "#ffffff";
  };

  // Template for timeline column
  const timelineBodyTemplate = (rowData) => {
    return (
      <div>
        <div>Start: {formatDate(rowData.start_date)}</div>
        <div>End: {formatDate(rowData.end_date)}</div>
      </div>
    );
  };

  // Template for actions column
  const actionsBodyTemplate = (rowData) => {
    return (
      <div className="d-flex gap-2">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-success p-button-sm"
          onClick={() => {
            setSelectedTask(rowData);
            setEditDialogVisible(true);
          }}
          tooltip="Edit"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-sync"
          className="p-button-rounded p-button-info p-button-sm"
          onClick={() => {
            setSelectedTask(rowData);
            setStatusDialogVisible(true);
          }}
          tooltip="Update Status"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-danger p-button-sm"
          onClick={() => confirmDelete(rowData)}
          tooltip="Delete"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  // Filter tasks based on search term and status
  const filteredTasks = tasks.filter((task) => {
    // Text search filter
    const matchesSearch =
      searchTerm === "" ||
      task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase()));

    // Status filter
    const matchesStatus = statusFilter === null || task.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  return (
    <div>
      <IsLogin />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Tasks</h5>
          <div className="d-flex justify-content-between align-items-center w-100 mt-2">
            <div className="d-flex align-items-center">
              <span className="p-input-icon-left mr-3" style={{ width: "300px" }}>
                <i className="pi pi-search" style={{ marginLeft: "4px" }} />
                <InputText
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search tasks..."
                  className="w-100"
                  style={{ paddingLeft: "20px" }}
                />
              </span>
              <Dropdown
                value={statusFilter}
                options={statusOptions}
                onChange={(e) => setStatusFilter(e.value)}
                placeholder="Filter by status"
                className="ml-2"
                style={{ width: "150px" }}
              />
            </div>
            <div>
              <Button label="Create Task" icon="pi pi-plus" className="p-button-success" onClick={() => setCreateDialogVisible(true)} />
            </div>
          </div>
        </div>

        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            <Toast ref={toast} />
            <ConfirmDialog />

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {loading ? (
              <div className="text-center p-5">
                <ProgressSpinner />
                <p className="mt-2">Loading tasks...</p>
              </div>
            ) : (
              <DataTable
                value={filteredTasks}
                paginator
                rows={100}
                rowsPerPageOptions={[100, 150, 250]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} tasks"
                responsiveLayout="scroll"
                emptyMessage="No tasks found"
                sortField="title"
                sortOrder={1}
                rowClassName={(rowData) => (highlightedItems[`task-${rowData.id}`] ? "highlight-row" : "")}
              >
                <Column field="title" header="Title" sortable />
                <Column body={taskTypeBodyTemplate} header="Task Type" sortable sortField="task_type.task_type" />
                <Column body={projectBodyTemplate} header="Project/Story" />
                <Column body={timelineBodyTemplate} header="Timeline" />
                <Column body={statusBodyTemplate} field="status" header="Status" sortable />
                <Column body={(rowData) => renderDevelopers(rowData.developers)} header="Developers" />
                <Column field="points" header="Points" sortable />
                <Column body={labelBodyTemplate} header="Label" />
                <Column body={actionsBodyTemplate} header="Actions" />
              </DataTable>
            )}
          </div>
        </div>
      </div>

      {/* Create Task Dialog */}
      <Dialog header="Create Task" visible={createDialogVisible} style={{ width: "80vw" }} onHide={() => setCreateDialogVisible(false)} maximizable>
        <Create
          setVisible={setCreateDialogVisible}
          onSuccess={(newTaskData) => {
            if (newTaskData && newTaskData.id) {
              handleAddNewTask(newTaskData);
            } else {
              setCreateDialogVisible(false);
            }
          }}
        />
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog header="Edit Task" visible={editDialogVisible} style={{ width: "80vw" }} onHide={() => setEditDialogVisible(false)} maximizable>
        {selectedTask && <Edit taskId={selectedTask.id} setVisible={setEditDialogVisible} onSuccess={handleUpdateTask} />}
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog header="Update Task Status" visible={statusDialogVisible} style={{ width: "50vw" }} onHide={() => setStatusDialogVisible(false)}>
        {selectedTask && (
          <StatusUpdate
            taskId={selectedTask.id}
            currentStatus={selectedTask.status}
            setVisible={setStatusDialogVisible}
            onSuccess={handleUpdateTaskStatus}
          />
        )}
      </Dialog>
    </div>
  );
};

export default List;
